import { EMPTY_ERROR_MESSAGE } from '@superdispatch/forms';
import { passwordCheckTypes } from 'shared/utils/PasswordUtils';
import { hasOnlyDigits } from 'shared/utils/StringUtils';
import { yupObject, yupString } from 'shared/utils/YupUtils';
import { InferType } from 'yup';
import { z } from 'zod';

export type LoginDTO = InferType<typeof loginYupSchema>;
export const loginYupSchema = yupObject({
  email: yupString()
    .trim()
    .required('Enter email')
    .email('Enter valid email. Example: name@company.com'),
  password: yupString().required('Enter password'),
});

export const resetPasswordSchema = yupObject({
  email: yupString()
    .required('Enter email')
    .email('Enter valid email. Example: name@company.com'),
});

export const confirmPasswordResetSchema = yupObject({
  password: yupString()
    .required(EMPTY_ERROR_MESSAGE)
    .min(8, EMPTY_ERROR_MESSAGE)
    .max(128)
    .matches(passwordCheckTypes.toLowerCaseAndUpperCase, EMPTY_ERROR_MESSAGE)
    .matches(passwordCheckTypes.toSpecialCharacter, EMPTY_ERROR_MESSAGE)
    .matches(passwordCheckTypes.toNumber, EMPTY_ERROR_MESSAGE),
});

export const resetPasswordSuccessSchema = yupObject({
  token: yupString(),
  has_dispatcher_role: yupString().nullable(),
});

export type VerifyAccountDTO = InferType<typeof verifyAccountYupSchema>;
export const verifyAccountYupSchema = yupObject({
  code: yupString()
    .required('Enter Verification Code')
    .length(6, 'Your code must be composed of 6 digits')
    .test('only digits', 'Your code must contain only digits', hasOnlyDigits),
  email: yupString().notRequired(),
  channel: yupString(),
});

export type ResendVerificationCodeDTO = InferType<typeof resendCodeSchema>;
const resendCodeSchema = yupObject({
  email: yupString().required(),
  channel: yupString().required(),
});

const authString = z
  .string()
  .nullish()
  .transform((value) => value ?? '');

export const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter email')
    .refine(
      (value) => z.email().safeParse(value).success,
      'Enter valid email. Example: name@company.com',
    )
    .prefault(''),
  password: z.string().min(1, 'Enter password').prefault(''),
});

export const verifyAccountSchema = z.object({
  code: z
    .string()
    .min(1, 'Enter Verification Code')
    .length(6, 'Your code must be composed of 6 digits')
    .refine(hasOnlyDigits, 'Your code must contain only digits')
    .prefault(''),
  email: authString,
  channel: authString,
});
